import { FC } from 'react';
import InputControl from '@/components/form/input-control';

import { Box, Grid, Stack, Theme } from '@mui/material';
import FormSection from '@/components/form/form-section';
import useFormHandle from '@/hooks/use-form-handle.hook';
import { useQuery } from 'react-query';
import useQueryHelper from '@/hooks/use-query-helper';
import { OrgModel } from '@/lib/models/org/org.model';
import { getOrg } from '@/lib/services/org.service';
import { QueryKey } from '@/lib/query-client';
import { Form } from '@/components/form';
import StatusChip from '@/components/chip/status-chip';
import Text from '@/components/text';
import Chip from '@/components/chip';
import useIntercom from '@/hooks/use-intercom.hook';
import FlatButton from '@/components/flat-button';
import Card from '@/components/card';
import { createUseStyles } from 'react-jss';
import { DATA_RETENTION_SETTINGS } from '@/lib/models/org';
import InlineToast from '@/components/toasts/inline';
import FormSectionSubtitle from '@/components/form/form-section/form-section-subtitle';

const fields = ['name', 'domains'];
const formModel = new OrgModel();

const useStyles = createUseStyles((theme: Theme) => ({
  listItem: {
    '&:not(:last-child)': {
      borderBottom: `1px solid ${theme.palette.divider}`,
      marginBottom: 8,
      paddingBottom: 8,
    },
  },
}));

const ProfileForm: FC = () => {
  const styles = useStyles();
  const formHandle = useFormHandle({
    initialValues: formModel,
    validationSchema: formModel.schemaSlice(fields),
    onSubmit: () => {
      console.error('no writeable fields in this form');
    },
  });

  const { showNewMessage } = useIntercom();

  const query = useQuery([QueryKey.OrgView], async () => getOrg());
  const { showLoader } = useQueryHelper(query);
  const { data: org } = query;

  if (showLoader || !org) {
    return null;
  }

  const { disablePromptRetention, domains, hasZdr } = org;

  const handleSupportTicket = () => {
    showNewMessage(
      'Hello, I would like to open a support ticket to change the User Activity Data Retention settings for my organization. Thank you!'
    );
  };

  return (
    <Box maxWidth="100%">
      <Box mb={4}>
        <Form formHandle={formHandle} query={query}>
          <FormSection title="Profile Settings" scrollId="profile-settings">
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <InputControl
                  name="name"
                  label="Organization Name"
                  formHandle={formHandle}
                  readonly
                />
              </Grid>
              <Grid item xs={6}>
                <InputControl
                  name="modeLabel"
                  label="Organization Mode"
                  formHandle={formHandle}
                  readonly
                />
              </Grid>

              <Grid item xs={12}>
                <Box mb={1}>
                  <Text bold>Domains:</Text>
                </Box>
                <Stack direction="row" gap={0.5} maxWidth="100%" flexWrap="wrap">
                  {domains.map((domain) => (
                    <Chip key={domain} label={domain} />
                  ))}
                </Stack>
              </Grid>
              <Grid item xs={6}></Grid>
              <Grid item xs={6}></Grid>
            </Grid>
          </FormSection>

          {disablePromptRetention && (
            <FormSection title="Prompt Retention">
              <FormSectionSubtitle>
                Prompt Retention determines whether user-generated GenAI prompts and model-generated
                responses are stored for review. When set to DISABLED, only request and response
                metadata will be stored.
              </FormSectionSubtitle>
              <Stack direction="row" gap={2}>
                <Box>Prompt Retention:</Box>
                <Box>
                  <StatusChip value="disabled" size="small" />
                </Box>
              </Stack>
            </FormSection>
          )}

          <FormSection title="User Event Retention">
            <FormSectionSubtitle>
              View the retention policy for the different types of user-generated data stored in our
              system. To make changes, please
              <FlatButton
                color="link"
                onClick={handleSupportTicket}
                label="contact support"
                inline
              />
              .
            </FormSectionSubtitle>
            {hasZdr && (
              <Box mb={2}>
                <InlineToast
                  level="warning"
                  message="WARNING: All user event data retention periods are currently set to zero, and no data is being saved. User event data will be sent to eligible telemetry destinations before being permanently removed."
                />
              </Box>
            )}
            <Card>
              {DATA_RETENTION_SETTINGS.map(({ key, label, description }) => {
                const retentionDays = org[key] ?? -1;
                const retentionLabel =
                  retentionDays === -1 ? 'Indefinite' : `${retentionDays} days`;
                return (
                  <Stack
                    key={key}
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    className={styles.listItem}
                  >
                    <Box pr={4}>
                      <Text>{label}</Text>
                      <Box mt={0.5}>
                        <Text size="small" color="grey">
                          {description}
                        </Text>
                      </Box>
                    </Box>
                    <Box>
                      <Chip
                        label={retentionLabel}
                        color={retentionDays <= 0 ? 'warning' : 'default'}
                      />
                    </Box>
                  </Stack>
                );
              })}
            </Card>
          </FormSection>
        </Form>
      </Box>
    </Box>
  );
};

export default ProfileForm;
